/**
 * The money spine in one widget: one artist, one client, one day.
 *
 * Every number below is computed with the same arithmetic the app uses at
 * checkout and at payout time, so the page cannot drift from the product:
 *   - the envelope split      src/domain/commissionEngine.ts computePayout
 *   - what lands in the till  src/domain/sessionFlow.ts cashInAud
 *   - the cash / card steer   src/domain/cashFlow.ts steerPayment
 *   - signing the envelope    src/domain/payoutFlow.ts signPayout
 *
 * The two lessons it is built to teach: moving the deposit slider never moves
 * the artist's envelope, and taking the balance on card puts nothing in the
 * till to pay them with.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { computePayout } from '@/domain/commissionEngine';
import { steerPayment } from '@/domain/cashFlow';
import type { CommissionArrangement } from '@/domain/types';
import { Playground, Segmented, Slider, Stat } from './primitives';
import { money } from './format';

type ArrangementKey = 'half' | 'sixty' | 'owner' | 'flat';

const ARRANGEMENTS: Record<ArrangementKey, { label: string; blurb: string; value: CommissionArrangement }> = {
  half: {
    label: '50 / 50 split',
    blurb: 'The house default. Half the session price to the artist.',
    value: { kind: 'percentage', artistShare: 0.5 },
  },
  sixty: {
    label: '60 / 40 split',
    blurb: 'A senior artist on a better split.',
    value: { kind: 'percentage', artistShare: 0.6 },
  },
  owner: {
    label: 'Owner artist',
    blurb: 'Keeps the lot. The studio takes nothing from their chair.',
    value: { kind: 'owner-artist' },
  },
  flat: {
    label: 'Flat $100 day fee',
    blurb: 'Paid the same whether the day takes $400 or $2,000.',
    value: { kind: 'flat-day-fee', feeAud: 100 },
  },
};

const ARRANGEMENT_OPTIONS = (Object.keys(ARRANGEMENTS) as ArrangementKey[]).map((k) => ({
  value: k,
  label: ARRANGEMENTS[k].label,
}));

export default function DayMoneyCalculator() {
  const [dayRate, setDayRate] = useState(1000);
  const [depositPaid, setDepositPaid] = useState(200);
  const [merch, setMerch] = useState(0);
  const [cashAlreadyIn, setCashAlreadyIn] = useState(0);
  const [arrangementKey, setArrangementKey] = useState<ArrangementKey>('half');
  const [method, setMethod] = useState<'cash' | 'card'>('cash');

  const arrangement = ARRANGEMENTS[arrangementKey];

  // Checkout, exactly as the floor computes it.
  const balanceDue = Math.max(0, dayRate - depositPaid);
  const collectNow = balanceDue + merch;
  const tillIn = method === 'cash' ? collectNow : 0;

  // The split runs on the session price. Merch is a separate line and never
  // enters the commissionable base.
  const breakdown = computePayout(arrangement.value, [
    { bookingId: 'demo', amountAud: dayRate },
    { bookingId: 'demo-merch', amountAud: merch, isMerch: true },
  ]);

  // The steer is read before this sale is taken: is there enough in the till
  // right now to cover what the artist is owed at the end of the day?
  const steer = steerPayment(cashAlreadyIn, breakdown.payoutAud);

  // Signing the envelope at the artist's finish. The envelope is paid whole or not
  // at all: if the till is short, NOTHING is handed over and the whole day waits
  // for the next payment that covers it (or the float is broken to close the gap).
  const workingCash = cashAlreadyIn + tillIn;
  const shortBy = Math.max(0, breakdown.payoutAud - workingCash);
  const leftInTill = shortBy > 0 ? workingCash : workingCash - breakdown.payoutAud;

  return (
    <Playground
      title="One artist, one client, one day"
      instruction="Drag anything. The three panels underneath recalculate with the same arithmetic the real checkout and payout screens use."
      footnote="Amounts are illustrative. The opening float is deliberately left out of every figure here, because the float is a sealed emergency reserve and is never treated as money you can spend."
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        {/* Controls */}
        <div className="space-y-5">
          <Slider
            label="Session price"
            value={dayRate}
            min={200}
            max={2400}
            step={50}
            onChange={setDayRate}
            format={money}
            hint="What the artist is charging for the day."
          />
          <Slider
            label="Deposit already paid"
            value={depositPaid}
            min={0}
            max={800}
            step={50}
            onChange={setDepositPaid}
            format={money}
            hint="Taken weeks ago when the booking was made."
          />
          <Slider
            label="Merch on the way out"
            value={merch}
            min={0}
            max={300}
            step={10}
            onChange={setMerch}
            format={money}
            hint="Balm, a t-shirt, aftercare."
          />
          <Slider
            label="Cash in the till before this sale"
            value={cashAlreadyIn}
            min={0}
            max={2000}
            step={50}
            onChange={setCashAlreadyIn}
            format={money}
            hint="What earlier clients have already handed over today."
          />
          <div className="pt-1">
            <Segmented
              label="The artist's arrangement"
              value={arrangementKey}
              options={ARRANGEMENT_OPTIONS}
              onChange={setArrangementKey}
            />
            <p className="font-serif text-cream-faint text-xs mt-2 leading-relaxed">{arrangement.blurb}</p>
          </div>
          <Segmented
            label="How the client pays the balance"
            value={method}
            options={[
              { value: 'cash', label: 'Cash' },
              { value: 'card', label: 'Card' },
            ]}
            onChange={setMethod}
          />
        </div>

        {/* Results */}
        <div className="space-y-4">
          <ResultBlock
            step="1"
            title="At the counter"
            note="The deposit comes off the price. Merch is added on top."
          >
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <Stat label="Session price" value={money(dayRate)} />
              <Stat label="Less deposit" value={`- ${money(depositPaid)}`} />
              <Stat label="Balance due" value={money(balanceDue)} tone="gold" />
              <Stat label="Plus merch" value={`+ ${money(merch)}`} />
              <Stat label="Collect now" value={money(collectNow)} strong tone="gold" />
              <Stat
                label="Into the till"
                value={money(tillIn)}
                tone={method === 'cash' ? 'mint' : 'blood'}
                caption={method === 'cash' ? 'Cash goes straight in.' : 'Card settles to the bank, not the till.'}
              />
            </div>
          </ResultBlock>

          <ResultBlock
            step="2"
            title="What the artist is owed"
            note="Worked out from the session price, never from what the client actually handed over today."
          >
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <Stat label="Commissionable" value={money(breakdown.grossAud)} caption="Merch excluded." />
              <Stat label="Artist envelope" value={money(breakdown.payoutAud)} strong tone="violet" />
              <Stat
                label="House share"
                value={money(breakdown.houseShareAud)}
                tone={breakdown.houseShareAud < 0 ? 'blood' : undefined}
                caption={breakdown.houseShareAud < 0 ? 'The flat fee costs more than the day earned.' : undefined}
              />
            </div>
            <p className="font-serif text-cream-soft text-sm mt-3 leading-relaxed">
              Move the deposit slider and watch this envelope stay exactly where it is. The deposit changes{' '}
              <em className="text-cream">when</em> the money arrives, not <em className="text-cream">how much</em> the
              artist earns. Merch is missing from the envelope on purpose: the studio keeps merch whole.
            </p>
          </ResultBlock>

          <ResultBlock
            step="3"
            title="Can you actually pay them?"
            note="At the artist's finish you hand over an envelope of cash. It has to come from somewhere, and it is paid whole or not at all."
          >
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <Stat label="Cash in the till" value={money(workingCash)} />
              <Stat label="Envelope owed" value={money(breakdown.payoutAud)} />
              <Stat
                label={shortBy > 0 ? 'Short by' : 'Left over'}
                value={money(shortBy > 0 ? shortBy : leftInTill)}
                strong
                tone={shortBy > 0 ? 'blood' : 'mint'}
                caption={
                  shortBy > 0
                    ? 'Nobody is part-paid: the whole day waits, or you break the float.'
                    : 'The aim is close to zero, with the float untouched.'
                }
              />
            </div>

            <div
              className={clsx(
                'mt-3 rounded-md border px-4 py-3',
                steer === 'cash' ? 'border-orange/40 bg-orange/10' : 'border-mint/40 bg-mint/10',
              )}
            >
              <p className={clsx('label-small-caps', steer === 'cash' ? 'text-orange' : 'text-mint')}>
                {steer === 'cash' ? 'Ask this client for cash' : 'Card is fine for this client'}
              </p>
              <p className="font-serif text-cream-soft text-sm mt-1.5 leading-relaxed">
                {steer === 'cash' ? (
                  <>
                    The artist is owed {money(breakdown.payoutAud)} and there is only {money(cashAlreadyIn)} in the till.
                    Until the day is covered, the floor nudges you to take cash.
                  </>
                ) : (
                  <>
                    There is already {money(cashAlreadyIn)} in the till against an envelope of{' '}
                    {money(breakdown.payoutAud)}. The day is covered, so card costs you nothing and saves the handling.
                  </>
                )}
              </p>
            </div>
          </ResultBlock>
        </div>
      </div>
    </Playground>
  );
}

function ResultBlock({
  step,
  title,
  note,
  children,
}: {
  step: string;
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gold-dim/20 bg-ink-deep/60 p-4">
      <div className="flex items-baseline gap-2.5">
        <span className="data-mono text-xs text-gold-bright">{step}</span>
        <h4 className="font-inter text-sm font-semibold text-cream">{title}</h4>
      </div>
      <p className="font-serif text-cream-faint text-xs mt-1 leading-relaxed">{note}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}
